/**
 * A transport-free contract for one family of personal preferences.
 *
 * The contract version belongs to the CODEC, not to the stored JSON. Existing
 * `core.user_setting` documents predate this primitive and keep their exact
 * wire shape. A family that needs a stored schema version still owns that
 * migration itself.
 */
export const PREFERENCE_CONTRACT_VERSION = 1 as const

export interface PreferenceDefinition<Address, Value, Update> {
  readonly contractVersion: typeof PREFERENCE_CONTRACT_VERSION
  readonly settingKey: string
  readonly read: (settings: Record<string, unknown> | undefined, address: Address) => Value
  readonly reduce: (value: Value, update: Update) => Value
  /** Return the complete value for this one `core.user_setting` key. */
  readonly write: (
    settings: Record<string, unknown>, address: Address, updates: readonly Update[],
  ) => unknown
}

export interface EntityPreferenceAddress { entity: string }

export interface SlotPreferenceUpdate<Value> {
  slot: string
  /** `null` removes this person's statement for the slot. */
  value: Value | null
}

const record = (value: unknown): Record<string, unknown> =>
  value != null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {}

/**
 * Define an entity -> optional envelope -> leaf-map preference grammar.
 *
 * Reading admits only values the family codec understands. Writing patches the
 * addressed leaves into the raw document, so unknown values and other entities
 * survive an edit by an older reader. `null` preserves a direct entity map.
 */
export function defineEntityLeafPreference<Value>({
  settingKey, decode, envelope = 'slots',
}: {
  settingKey: string
  decode: (value: unknown) => Value | null
  envelope?: string | null
}): PreferenceDefinition<EntityPreferenceAddress, Record<string, Value>, SlotPreferenceUpdate<Value>> {
  return {
    contractVersion: PREFERENCE_CONTRACT_VERSION,
    settingKey,
    read: (settings, address) => {
      const all = record(settings?.[settingKey])
      const entity = record(all[address.entity])
      const leaves = envelope === null ? entity : record(entity[envelope])
      const out: Record<string, Value> = {}
      for (const [slot, raw] of Object.entries(leaves)) {
        const value = decode(raw)
        if (value !== null) out[slot] = value
      }
      return out
    },
    reduce: (current, update) => {
      if (!update.slot) return current
      const next = { ...current }
      if (update.value === null) delete next[update.slot]
      else next[update.slot] = update.value
      return next
    },
    write: (settings, address, updates) => {
      const all = record(settings[settingKey])
      const previous = record(all[address.entity])
      const leaves = { ...(envelope === null ? previous : record(previous[envelope])) }
      for (const update of updates) {
        if (!update.slot) continue
        if (update.value === null) delete leaves[update.slot]
        else leaves[update.slot] = update.value
      }
      return {
        ...all,
        [address.entity]: envelope === null ? leaves : { ...previous, [envelope]: leaves },
      }
    },
  }
}

/** Define an entity -> leaf-map preference without adding a storage envelope. */
export function defineEntityMapPreference<Value>(options: {
  settingKey: string
  decode: (value: unknown) => Value | null
}) {
  return defineEntityLeafPreference({ ...options, envelope: null })
}

/** Define the common entity -> `slots` envelope preference grammar. */
export const defineEntitySlotsPreference = defineEntityLeafPreference
